import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { Eye, EyeOff, ShieldCheck, CircleCheck } from 'lucide-react';
import Turnstile from '../components/Turnstile';

/* ----------------------------------------------------------------------------
 * Register — neutral base + single signal-green accent, editorial serif
 * heading, hairline-bordered card. Matches Home.js design system. No purple.
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useSupabaseAuth();
  const [turnstileToken, setTurnstileToken] = useState('');

  const location = useLocation();
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const session = params.get('session_id');

    if (session) {
      setSessionId(session);
      toast.success('Payment verified! Create your account to enable Growth features.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(
        formData.name,
        formData.email,
        formData.password,
        formData.company,
        sessionId,
        turnstileToken
      );

      if (result.success) {
        toast.success('Account created! Welcome to AutoHire.');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const result = await loginWithGoogle(credentialResponse.credential, sessionId);
      if (result.success) {
        toast.success('Signed up with Google!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Google sign up failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Google sign up failed');
    }
  };

  const inputClass =
    'appearance-none block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3.5 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm transition-colors focus:border-[#2563EB] focus:outline-none focus:ring-1 focus:ring-[#2563EB]';

  const PERKS = [
    'Proof-of-skill scoring',
    'Explainable match scores',
    'Every decision logged',
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="mb-1 inline-flex w-full items-center justify-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} />
          Verified talent platform
        </div>
      </motion.div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-8 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)] sm:px-10"
        >
          <Link to="/" className="flex justify-center">
            <img className="h-20 w-auto" src="/AutoHire.png" alt="AutoHire" />
          </Link>
          <h2 className="mt-5 text-center font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-[#1D4FD8] hover:text-[#1D4FD8]">
              Sign in
            </Link>
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full name
              </label>
              <div className="mt-1.5">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <div className="mt-1.5">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company name
              </label>
              <div className="mt-1.5">
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1.5 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClass}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Turnstile onToken={setTurnstileToken} />

            <div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                style={{ background: GREEN }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = '#1D4FD8')}
                onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-800" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  toast.error('Google sign up failed');
                }}
                theme="outline"
                shape="pill"
                width="100%"
                useOneTap
              />
            </div>
          </div>

          <div className="mt-7 border-t border-gray-100 dark:border-gray-800 pt-5">
            <ul className="space-y-2">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-center gap-2 text-[13px] text-gray-600 dark:text-gray-400">
                  <CircleCheck size={15} style={{ color: GREEN }} />
                  {perk}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.p
          {...reveal}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-6 flex items-center justify-center gap-1.5 text-xs text-gray-400"
        >
          <ShieldCheck size={13} style={{ color: GREEN }} />
          Every decision logged and reviewable
        </motion.p>
      </div>
    </div>
  );
}

export default Register;
